/*
 * dm-size-advisor.js
 * Size advisor popup (markup: snippets/dm-size-advisor.liquid, look: assets/dm-size-advisor.css).
 * Opens through the theme's modal manager (DM.modal: focus trap, Escape, scroll lock) from any [data-dm-open="dm-size-advisor"],
 * and from the "Size guide" links inside landing-page text (a.href = /pages/sizing), which still work as normal links without this file.
 *
 * HOW A SIZE IS PICKED (the only data used is the vest's own size chart, garment measurements in cm):
 *   room    = garment measurement - your body measurement, for chest and for waist (both must be met)
 *   needed  = (fit + layer) x STEP, where STEP is the chart's own typical gap between two sizes (median of the chart's
 *             adjacent-size differences = 8 cm today). fit: Close 0, Regular 0.5, Relaxed 1. layer: thin 0, knit 0.5.
 *   result  = the smallest size on the chart whose chest room and waist room are both >= needed; if none, the largest size.
 * Assumptions (not supplier data): the chart lists full garment circumferences; the Close/Regular/Relaxed and layer steps
 * above are our own design choice expressed in the chart's own units. Nothing here is a body-size chart.
 *
 * PRIVACY: no localStorage / sessionStorage / cookies, no fetch/beacon/analytics call carries the measurements. The only network
 * call is the normal DMCart.add with the variant id and quantity. State lives in this closure and is gone on reload.
 */
(function () {
  'use strict';
  var doc = document;
  var MODAL_ID = 'dm-size-advisor';
  var FIT = { close: 0, regular: 0.5, relaxed: 1 };
  var FIT_NAME = { close: 'close', regular: 'regular', relaxed: 'relaxed' };
  var LAYER = { thin: 0, knit: 0.5 };
  var LAYER_NAME = { thin: 'a thin layer', knit: 'a knit layer' };
  // slider / typing limits only (what the controls accept), not a size rule
  var LIM = {
    cm: { chest: [70, 140], waist: [55, 130], step: 1 },
    'in': { chest: [28, 55], waist: [22, 51], step: 0.5 }
  };
  var CM_PER_IN = 2.54;
  var STEP_LABELS = ['', 'Step 1 of 3 · Measurements', 'Step 2 of 3 · Layers', 'Step 3 of 3 · Fit preference', 'Your recommendation'];

  var DM = null;
  var modal, panel, data, chart, STEP, root, colorIdx, sizeIdx;
  var st = { unit: 'cm', chest: 100, waist: 85, touched: { chest: false, waist: false }, layer: 'thin', fit: 'regular', step: 1, opener: null, fromPicker: false, committed: false, adding: false, target: null };

  function q(sel, ctx) { return (ctx || modal).querySelector(sel); }
  function qa(sel, ctx) { return Array.prototype.slice.call((ctx || modal).querySelectorAll(sel)); }
  function money(c) { return DM && DM.money ? DM.money(c) : '$' + (c / 100).toFixed(2); }
  function median(a) { var s = a.slice().sort(function (x, y) { return x - y; }); var m = Math.floor(s.length / 2); return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; }
  function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }
  function num(v, d) { var s = (Math.round(v * Math.pow(10, d)) / Math.pow(10, d)).toString(); return s; }
  function toUnit(cm) { return st.unit === 'in' ? cm / CM_PER_IN : cm; }
  function fromUnit(v) { return st.unit === 'in' ? v * CM_PER_IN : v; }
  function lenText(o) { return st.unit === 'in' ? (o['in'] || num(o.cm / CM_PER_IN, 1) + ' in') : num(o.cm, 1) + ' cm'; }
  function amount(cm) { var a = Math.abs(cm); return st.unit === 'in' ? num(a / CM_PER_IN, 1) + ' in' : num(a, 0) + ' cm'; }

  /* ---------- the rule ---------- */
  function recommend(chestCm, waistCm, fit, layer) {
    var need = (FIT[fit] + LAYER[layer]) * STEP;
    var EPS = 0.001, i, ic = -1, iw = -1, pick = -1;
    for (i = 0; i < chart.length; i++) {
      var okC = chart[i].chest.cm - chestCm >= need - EPS;
      var okW = chart[i].waist.cm - waistCm >= need - EPS;
      if (okC && ic === -1) ic = i;
      if (okW && iw === -1) iw = i;
      if (okC && okW) { pick = i; break; }
    }
    var out = pick === -1;
    if (out) pick = chart.length - 1;
    var row = chart[pick];
    var roomC = row.chest.cm - chestCm, roomW = row.waist.cm - waistCm;
    var driver = 'both';
    if (!out && ic !== iw) driver = ic > iw ? 'chest' : 'waist';
    if (out) driver = roomC - need < roomW - need ? 'chest' : 'waist';
    var extra = Math.min(roomC, roomW) - need;
    return { i: pick, row: row, out: out, need: need, roomC: roomC, roomW: roomW, driver: driver, roomy: pick === 0 && extra > STEP / 2 };
  }
  function band(roomCm) {
    var r = roomCm / STEP;
    if (r < 0) return 'Smaller than you measured';
    if (r < 0.25) return 'Close';
    if (r < 0.75) return 'Regular';
    if (r < 1.25) return 'Relaxed';
    return 'Roomy';
  }

  /* ---------- page selection (the PDP form or the landing-page picker) ---------- */
  function radios(i) { return root ? qa('.dm-opt__input[data-option-index="' + i + '"]', root) : []; }
  function chosenOn(i) {
    var r = radios(i).filter(function (x) { return x.checked; })[0];
    return r ? r.value : null;
  }
  function selectionFor(size) {
    return data.options.map(function (o, i) {
      if (i === sizeIdx) return size;
      return chosenOn(i) || o.values[0];
    });
  }
  function variantFor(sel) {
    return data.variants.filter(function (v) { return sel.every(function (s, i) { return v.options[i] === s; }); })[0] || null;
  }
  function selectOnPage(size) {
    var r = radios(sizeIdx).filter(function (x) { return x.value === size; })[0];
    if (!r || r.disabled) return false;
    r.checked = true;
    r.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }
  function pageQty() {
    var el = root && root.querySelector ? root.querySelector('[data-dm-qty]') : null;
    var n = el ? parseInt(el.value, 10) : 1;
    return isNaN(n) || n < 1 ? 1 : n;
  }

  /* ---------- rendering ---------- */
  function setStep(n, focus) {
    st.step = n;
    qa('[data-adv-step]').forEach(function (s) { s.hidden = String(n) !== s.getAttribute('data-adv-step'); });
    qa('[data-adv-acts]').forEach(function (s) { s.hidden = String(n) !== s.getAttribute('data-adv-acts'); });
    qa('[data-adv-seg]').forEach(function (s, i) { s.classList.toggle('is-on', n === 4 || i < n); });
    q('[data-adv-stepno]').textContent = STEP_LABELS[n];
    if (n === 4) renderResult();
    if (n === 1) renderMeasure();
    if (focus !== false) {
      panel.scrollTop = 0;
      var h = q('[data-adv-step="' + n + '"] [data-adv-title]');
      if (h) h.focus({ preventScroll: true });
    }
  }

  function renderMeasure() {
    var lim = LIM[st.unit];
    ['chest', 'waist'].forEach(function (k) {
      var range = q('[data-adv-range="' + k + '"]');
      var input = q('[data-adv-num="' + k + '"]');
      var lo = lim[k][0], hi = lim[k][1];
      range.min = lo; range.max = hi; range.step = lim.step;
      var v = clamp(toUnit(st[k]), lo, hi);
      range.value = v;
      range.style.setProperty('--p', ((v - lo) / (hi - lo)) * 100 + '%');
      range.classList.toggle('is-untouched', !st.touched[k]);
      range.setAttribute('aria-valuetext', num(v, 1) + ' ' + st.unit);
      input.min = lo; input.max = hi; input.step = 'any';
      if (doc.activeElement !== input) input.value = num(toUnit(st[k]), 1);
    });
    qa('[data-adv-unitlabel]').forEach(function (u) { u.textContent = st.unit; });
    qa('[data-adv-unit]').forEach(function (b) { b.setAttribute('aria-pressed', String(b.getAttribute('data-adv-unit') === st.unit)); });
    var ready = st.touched.chest && st.touched.waist;
    q('[data-adv-acts="1"] [data-adv-next]').disabled = !ready;
    var chip = q('[data-adv-chip]'), sub = q('[data-adv-livesub]');
    if (ready) {
      var r = recommend(st.chest, st.waist, st.fit, st.layer);
      chip.textContent = r.row.size;
      sub.textContent = r.out ? 'The largest size on the chart' : 'Refines in the next steps';
    } else {
      chip.textContent = '–';
      sub.textContent = 'Set both measurements to see it';
    }
  }

  function setNote(el, strong, text) {
    el.textContent = '';
    var b = doc.createElement('b'); b.textContent = strong;
    el.appendChild(b);
    el.appendChild(doc.createTextNode(text));
  }

  function renderResult() {
    var r = recommend(st.chest, st.waist, st.fit, st.layer);
    var size = r.row.size;
    q('[data-adv-size]').textContent = size;
    q('[data-adv-best]').textContent = r.out ? 'Closest fit' : 'Best fit';
    q('[data-adv-colsize]').textContent = 'Size ' + size;
    var why;
    var req = r.need > 0 ? amount(r.need) : '';
    var what = FIT_NAME[st.fit] + ' fit over ' + LAYER_NAME[st.layer];
    if (r.out) {
      why = size + ' is our largest size, and it leaves less room than a ' + what + ' needs' + (req ? ' (' + req + ')' : '') + '. It is the closest fit on the chart.';
    } else if (r.need === 0) {
      why = size + ' is the smallest size on the chart that is at least as big as your chest and waist, for a ' + what + '.';
    } else {
      why = size + ' is the smallest size on the chart that leaves at least ' + req + ' of room at your chest and waist, for a ' + what + '.';
    }
    if (!r.out && r.i > 0) {
      why += r.driver === 'both' ? '' : ' Your ' + r.driver + ' sets the size.';
    }
    if (r.roomy) why += ' ' + size + ' is our smallest size, so it may fit roomier than you chose.';
    q('[data-adv-why]').textContent = why;

    var arc = q('.dm-adv__arc'); arc.classList.remove('is-draw'); void arc.getBoundingClientRect(); arc.classList.add('is-draw');

    var cRow = q('[data-adv-area="chest"]'), wRow = q('[data-adv-area="waist"]');
    q('[data-adv-v]', cRow).textContent = lenText(r.row.chest);
    q('[data-adv-v]', wRow).textContent = lenText(r.row.waist);
    setNote(q('[data-adv-n]', cRow), band(r.roomC), r.roomC >= 0 ? amount(r.roomC) + ' of room' : amount(r.roomC) + ' short');
    setNote(q('[data-adv-n]', wRow), band(r.roomW), r.roomW >= 0 ? amount(r.roomW) + ' of room' : amount(r.roomW) + ' short');
    q('[data-adv-v]', q('[data-adv-area="length"]')).textContent = lenText(r.row.length);
    q('[data-adv-v]', q('[data-adv-area="shoulder"]')).textContent = lenText(r.row.shoulder);

    // availability in the colour currently chosen on the page
    var rec = size;
    var recVar = variantFor(selectionFor(rec));
    var target = rec, targetVar = recVar, msg = '';
    if (!recVar || !recVar.available) {
      var alt = null, k;
      for (k = r.i + 1; k < chart.length; k++) {
        var vv = variantFor(selectionFor(chart[k].size));
        if (vv && vv.available) { alt = chart[k]; targetVar = vv; break; }
      }
      var color = colorIdx > -1 ? (chosenOn(colorIdx) || data.options[colorIdx].values[0]) : '';
      var inOther = '';
      if (colorIdx > -1) {
        data.options[colorIdx].values.forEach(function (c) {
          if (c === color || inOther) return;
          var sel = selectionFor(rec); sel[colorIdx] = c;
          var ov = variantFor(sel);
          if (ov && ov.available) inOther = c;
        });
      }
      msg = 'Size ' + rec + (color ? ' in ' + color : '') + ' is sold out.' + (inOther ? ' It is in stock in ' + inOther + '.' : '') + (alt ? ' ' + alt.size + ' has more room and is in stock.' : '');
      if (alt) { target = alt.size; } else { target = null; targetVar = null; }
    }
    st.target = target;
    var btn = q('[data-adv-add]');
    btn.disabled = !target || st.adding;
    q('[data-adv-addlabel]').textContent = target ? 'Add size ' + target + ' to bag' : 'Sold out';
    q('[data-adv-addprice]').textContent = targetVar ? money(targetVar.price) : '';
    var priceEl = q('[data-adv-price]');
    if (targetVar) priceEl.textContent = money(targetVar.price);

    // size scale
    var ul = q('[data-adv-scale]'); ul.textContent = '';
    chart.forEach(function (c, i) {
      var li = doc.createElement('li');
      li.textContent = c.size;
      var v = variantFor(selectionFor(c.size));
      if (i === r.i) li.classList.add('is-rec');
      if (v && !v.available) {
        li.classList.add('is-out');
        var sr = doc.createElement('span'); sr.className = 'lux-sr'; sr.textContent = ' (sold out)'; li.appendChild(sr);
      }
      if (i === r.i) { var sr2 = doc.createElement('span'); sr2.className = 'lux-sr'; sr2.textContent = ' (recommended)'; li.appendChild(sr2); }
      ul.appendChild(li);
    });

    var more = q('[data-adv-more]');
    var next = chart[r.i + 1];
    if (r.out) {
      more.textContent = 'You are at the edge of our size range. Check the full size chart before you order.';
    } else if (next) {
      more.textContent = 'Want more room? ' + next.size + ' adds ' + amount(next.chest.cm - r.row.chest.cm) + ' at the chest and ' + amount(next.waist.cm - r.row.waist.cm) + ' at the waist.';
    } else {
      more.textContent = '';
    }
    var err = q('[data-adv-err]');
    err.hidden = !msg; err.textContent = msg;
  }

  /* ---------- actions ---------- */
  function showError(t) { var e = q('[data-adv-err]'); e.textContent = t; e.hidden = false; }

  function openerForCart() {
    var o = st.opener;
    if (o && doc.contains(o) && !o.closest('#' + MODAL_ID) && !o.closest('#dm-sticky-picker')) return o;
    return doc.querySelector('[data-dm-sticky-atc] [data-dm-open="dm-sticky-picker"]') || doc.querySelector('[data-dm-add]') || doc.body;
  }

  function addToBag() {
    if (st.adding || !st.target) return;
    var sel = selectionFor(st.target);
    var v = variantFor(sel);
    if (!v || !v.available) { renderResult(); return; }
    selectOnPage(st.target);
    if (!window.DMCart) { showError((DM.strings && DM.strings.addError) || 'Something went wrong. Please try again.'); return; }
    st.adding = true; st.committed = true;
    q('[data-adv-add]').disabled = true;
    q('[data-adv-addlabel]').textContent = 'Adding…';
    window.DMCart.add([{ id: v.id, quantity: pageQty() }], { source: data.mode === 'pdp' ? 'product' : 'sticky-bar', opener: openerForCart() })
      .then(function () {
        st.adding = false;
        if (DM.modal.isOpen(modal)) DM.modal.close(modal, { restoreFocus: false });
      })
      .catch(function (err) {
        st.adding = false; st.committed = false;
        renderResult();
        if (err && err.message === 'busy') return;
        showError((DM.strings && DM.strings.addError) || 'Something went wrong. Please try again.');
      });
  }

  function justSelect() {
    if (!st.target) return;
    if (selectOnPage(st.target)) {
      st.committed = true;
      if (DM.announce) DM.announce('Size ' + st.target + ' selected');
      DM.modal.close(modal);
    } else {
      showError('That size is not available right now.');
    }
  }

  function nudge(k, dir) {
    var stepCm = fromUnit(LIM[st.unit].step) ;
    st[k] = clamp(st[k] + dir * stepCm, fromUnit(LIM[st.unit][k][0]), fromUnit(LIM[st.unit][k][1]));
    st.touched[k] = true;
    renderMeasure();
  }

  function onNumber(k, finalize) {
    var input = q('[data-adv-num="' + k + '"]');
    var v = parseFloat(String(input.value).replace(',', '.'));
    var lo = LIM[st.unit][k][0], hi = LIM[st.unit][k][1];
    if (!isFinite(v)) { if (finalize) renderMeasure(); return; }
    if (finalize) v = clamp(v, lo, hi);
    if (v < lo || v > hi) return;
    st[k] = fromUnit(v);
    st.touched[k] = true;
    if (finalize) input.blur();
    renderMeasure();
  }

  function setUnit(u) {
    if (u === st.unit) return;
    st.unit = u;
    renderMeasure();
  }

  function openAdvisor() {
    var mode = data.mode;
    root = mode === 'pdp' ? doc.querySelector('dm-product') : doc.getElementById('dm-sticky-picker');
    var o = modal.__dmOpener;
    st.opener = o || null;
    st.fromPicker = !!(o && o.closest && o.closest('#dm-sticky-picker'));
    st.committed = false; st.adding = false;
    q('[data-adv-err]').hidden = true;
    // header price: the chosen size's price if one is chosen on the page, else the lowest price
    var sz = chosenOn(sizeIdx);
    var v = sz ? variantFor(selectionFor(sz)) : null;
    var prices = data.variants.map(function (x) { return x.price; });
    var lo = Math.min.apply(null, prices), hi = Math.max.apply(null, prices);
    var fromText = lo === hi ? money(lo) : (DM.fmt ? DM.fmt((DM.strings && DM.strings.from) || 'From {price}', { price: money(lo) }) : 'From ' + money(lo));
    q('[data-adv-price]').textContent = v ? money(v.price) : (prices.length ? fromText : '');
    st.fit = (q('input[name="dm-adv-fit"]:checked') || {}).value || 'regular';
    st.layer = (q('input[name="dm-adv-layer"]:checked') || {}).value || 'thin';
    setStep(1, false);
  }

  var booted = false;
  function init() {
    if (booted) return;
    DM = window.DM;
    modal = doc.getElementById(MODAL_ID);
    var raw = doc.getElementById('dm-size-advisor-data');
    if (!modal || !raw || !DM || !DM.modal) return;
    try { data = JSON.parse(raw.textContent); } catch (e) { return; }
    panel = q('.dm-adv__panel');
    chart = (data.chart || []).filter(function (r) {
      return r.size && r.chest && r.waist && isFinite(r.chest.cm) && isFinite(r.waist.cm) && r.chest.cm > 0 && r.waist.cm > 0;
    }).sort(function (a, b) { return a.chest.cm - b.chest.cm || a.waist.cm - b.waist.cm; });
    if (chart.length < 2) return;
    var diffs = [];
    for (var i = 1; i < chart.length; i++) { diffs.push(chart[i].chest.cm - chart[i - 1].chest.cm, chart[i].waist.cm - chart[i - 1].waist.cm); }
    STEP = median(diffs.filter(function (d) { return d > 0; }));
    data.options.forEach(function (o, i) {
      if (/^colou?r$/i.test(o.name)) colorIdx = i;
      if (/^size$/i.test(o.name)) sizeIdx = i;
    });
    if (colorIdx === undefined) colorIdx = -1;
    if (sizeIdx === undefined) return;

    modal.addEventListener('dm:modal:open', function (e) { if (e.target === modal) openAdvisor(); });
    modal.addEventListener('dm:modal:close', function (e) {
      if (e.target !== modal) return;
      if (st.fromPicker && !st.committed) {
        var picker = doc.getElementById('dm-sticky-picker');
        var op = st.opener;
        st.fromPicker = false;
        if (picker) window.setTimeout(function () { if (!DM.modal.top()) DM.modal.open(picker, op); }, 30);
      }
    });

    modal.addEventListener('click', function (e) {
      var t = e.target instanceof Element ? e.target : null;
      if (!t) return;
      var b;
      if ((b = t.closest('[data-adv-unit]'))) { setUnit(b.getAttribute('data-adv-unit')); return; }
      if ((b = t.closest('[data-adv-nudge]'))) { nudge(b.getAttribute('data-adv-nudge'), parseInt(b.getAttribute('data-dir'), 10)); return; }
      if (t.closest('[data-adv-next]')) { if (st.step < 4) setStep(st.step + 1); return; }
      if (t.closest('[data-adv-back]')) { if (st.step > 1) setStep(st.step - 1); return; }
      if (t.closest('[data-adv-restart]')) { setStep(1); return; }
      if (t.closest('[data-adv-add]')) { addToBag(); return; }
      if (t.closest('[data-adv-select]')) { justSelect(); return; }
      if ((b = t.closest('[data-dm-open]')) && b.getAttribute('data-dm-open') === data.chartModal && data.chartModal) {
        // keep focus return pointing at the original "Size guide" link, not at this (closing) popup
        var chartEl = doc.getElementById(data.chartModal);
        if (chartEl) { e.stopPropagation(); e.preventDefault(); DM.modal.open(chartEl, st.opener || b); }
      }
    });
    modal.addEventListener('input', function (e) {
      var t = e.target;
      if (!(t instanceof HTMLInputElement)) return;
      var k;
      if ((k = t.getAttribute('data-adv-range'))) {
        st[k] = fromUnit(parseFloat(t.value)); st.touched[k] = true; renderMeasure();
      } else if ((k = t.getAttribute('data-adv-num'))) {
        onNumber(k, false);
      } else if (t.name === 'dm-adv-fit') { st.fit = t.value; }
      else if (t.name === 'dm-adv-layer') { st.layer = t.value; }
    });
    modal.addEventListener('change', function (e) {
      var t = e.target;
      if (!(t instanceof HTMLInputElement)) return;
      var k = t.getAttribute('data-adv-num');
      if (k) onNumber(k, true);
      if (t.name === 'dm-adv-fit') st.fit = t.value;
      if (t.name === 'dm-adv-layer') st.layer = t.value;
    });
    modal.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      var t = e.target;
      if (t instanceof HTMLInputElement && t.hasAttribute('data-adv-num')) {
        e.preventDefault();
        onNumber(t.getAttribute('data-adv-num'), true);
        var nx = q('[data-adv-acts="1"] [data-adv-next]');
        if (nx && !nx.disabled) setStep(2);
      }
    });
    modal.addEventListener('focusin', function (e) {
      var row = e.target.closest ? e.target.closest('[data-adv-row]') : null;
      var m = q('.dm-adv__measure');
      if (m) m.setAttribute('data-active', row ? row.getAttribute('data-adv-row') : '');
    });

    // "Size guide" text links inside landing pages open the popup; they stay normal links to /pages/sizing otherwise.
    doc.addEventListener('click', function (e) {
      var t = e.target instanceof Element ? e.target : null;
      var a = t && t.closest('.lp-note a[href="/pages/sizing"], a[data-dm-adv-link]');
      if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button) return;
      e.preventDefault();
      DM.modal.open(modal, a);
    });

    window.DMSizeAdvisor = {
      open: function (opener) { DM.modal.open(modal, opener); },
      recommend: function (chestCm, waistCm, fit, layer) { var r = recommend(chestCm, waistCm, fit || 'regular', layer || 'thin'); return r.row.size + (r.out ? '+' : ''); },
      step: function () { return STEP; }
    };
    booted = true;
    renderMeasure();
  }

  // This file is a deferred script that sits earlier in the page than dm-global.js, so DM may not exist yet when it runs:
  // wait for DOMContentLoaded (after every deferred script has run); init() is safe to call more than once.
  if (window.DM && window.DM.modal) init();
  else {
    doc.addEventListener('DOMContentLoaded', init);
    window.addEventListener('load', init);
  }
})();
